import { LitElement, html, css, unsafeCSS } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import styles from './scrollPanel.scss?inline';
import SimpleBar from 'simplebar';
import debounce from 'lodash/debounce';

export default class SotbitScrollPanel extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        disabled: { type: Boolean },
        sticky: { type: Boolean },
    };

    constructor() {
        super();
        this.contentRef = createRef();
        this.simpleBarInstance = null;
        this.disabled = false;
        this.sticky = false;
        this.positionFixedHandler = this.stickyHandler.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        this.simpleBarInstance?.init();
    }

    async updated() {
        if (!this.disabled && this.contentRef.value && !this.simpleBarInstance) {
            this.simpleBarInstance?.getScrollElement()?.removeEventListener('scroll', this._dispatchScroll);
            this.simpleBarInstance = new SimpleBar(this.contentRef.value, { autoHide: false });
            this.simpleBarInstance?.getScrollElement()?.setAttribute('part', 'scroll-content-wrapper');
            this.simpleBarInstance?.getScrollElement().addEventListener('scroll', this._dispatchScroll);
            this.simpleBarInstance?.axis?.y?.track?.el?.setAttribute('part', 'track-vertical');
            this.simpleBarInstance?.axis?.y?.scrollbar?.el?.setAttribute('part', 'scrollbar-vertical');
        }

        if (this.sticky && !this.disabled) {
            this.stickyHandler();
            this.setListeners();
        }
    }

    requestUpdate(name, oldValue, options) {
        super.requestUpdate(name, oldValue, options);

        if (name === 'disabled' && this.disabled) {
            this.simpleBarInstance?.unMount();
            this.simpleBarInstance = null;
        }
    }

    setStickyStyle(params = {}) {
        if (!this.simpleBarInstance) {
            return;
        }
        const { width = null, left = null } = params;
        this.simpleBarInstance.axis.x.track.el.style.width = typeof width === 'number' ? `${width}px` : width;
        this.simpleBarInstance.axis.x.track.el.style.left = typeof left === 'number' ? `${left}px` : left;
    }

    stickyHandler() {
        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom <= viewportHeight) {
            this.classList.remove('fixed');
            this.setStickyStyle();
        } else {
            this.setStickyStyle({ width: rect.width, left: rect.left });
            this.classList.add('fixed');
        }
    }

    setListeners() {
        window.addEventListener('scroll', this.positionFixedHandler);
        window.addEventListener('resize', this.positionFixedHandler);
    }

    removeListeners() {
        window.removeEventListener('scroll', this.positionFixedHandler);
        window.removeEventListener('resize', this.positionFixedHandler);
    }

    async attributeChangedCallback(name, _old, value) {
        super.attributeChangedCallback(name, _old, value);

        if (name === 'disabled') {
            await this.requestUpdate();

            if (value === null && !this.simpleBarInstance && this.contentRef.value) {
                this.simpleBarInstance = new SimpleBar(this.contentRef.value, {
                    autoHide: false,
                });
                return;
            }

            if (value !== null && this.simpleBarInstance) {
                this.simpleBarInstance?.unMount();
            }

            if (value === null && this.simpleBarInstance) {
                this.simpleBarInstance?.init();
            }
        }

        if (name === 'sticky') {
            if (value !== null) {
                this.removeListeners();
                this.setListeners();
            } else {
                this.removeListeners();
            }
        }
    }

    _dispatchScroll = (e) => {
        this.dispatchEvent(
            new CustomEvent('scroll', {
                bubbles: true,
                composed: true,
                detail: {
                    originalEvent: e,
                    target: this,
                    getScrollElement: this.simpleBarInstance?.getScrollElement(),
                },
            })
        );
    };

    async _handleSlotChange() {
        this._slotChangeDebounce();
    }

    _slotChangeDebounce = debounce(async () => {
        await this.getUpdateComplete();
        this.simpleBarInstance?.recalculate();

        if (this.sticky && !this.disabled) {
            this.stickyHandler();
        }
    }, 60);

    disconnectedCallback() {
        super.disconnectedCallback();
        this.simpleBarInstance?.unMount();
        this.removeListeners();
    }

    render() {
        return html`
            ${this.disabled
                ? html` <slot></slot>`
                : html` <div part="scroll-panel" class="scroll-panel" ${ref(this.contentRef)}>
                      <slot @slotchange="${this._handleSlotChange}"></slot>
                  </div>`}
        `;
    }
}
